import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { 
  Car, ShieldCheck, Search, Wrench, Truck, CheckCircle2, 
  Lock, User, Phone, MapPin, FileText, ChevronRight, 
  ArrowRight, RefreshCw, XCircle, LogOut, Settings, PlusCircle, 
  Eye, Check, AlertCircle, ChevronLeft, HelpCircle, Building2,
  Download, MessageCircle, Clock, History, CalendarClock, Pencil,
  Sun, Moon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const STAGES = [
  { id: 1, name: "1. Isi Form Registrasi", desc: "Registrasi awal calon kustomer", icon: FileText },
  { id: 2, name: "2. Registrasi Payment", desc: "Konfirmasi pembayaran biaya layanan", icon: ShieldCheck },
  { id: 3, name: "3. Konsultan Brief", desc: "Diskusi kebutuhan bersama konsultan", icon: User },
  { id: 4, name: "4. Dalam Proses", desc: "Pencarian, inspeksi, eksekusi & pelunasan", icon: Search },
  { id: 5, name: "5. Service & Perawatan", desc: "Pengecekan akhir & perbaikan minor", icon: Wrench },
  { id: 6, name: "6. Pengiriman & Serah Terima", desc: "Unit dikirim ke alamat kustomer", icon: Truck },
  { id: 7, name: "7. Selesai", desc: "Transaksi tuntas & garansi aktif", icon: CheckCircle2 }
];

const IN_PROCESS_SUBSTAGES = [
  "Pencarian Iklan",
  "Inspeksi Unit",
  "Eksekusi Unit & Pelunasan",
  "Cancel"
];

// --- WhatsApp deep-link helper ---
function buildWhatsAppUrl(order, current_stage, sub_stage) {
  let phone = (order.phone || "").replace(/\D/g, "");
  if (phone.startsWith("0")) phone = "62" + phone.slice(1);
  else if (!phone.startsWith("62")) phone = "62" + phone;

  const stageObj = STAGES.find(s => s.id === current_stage);
  const stageLabel = stageObj?.name.replace(/^\d+\.\s*/, "") || `Tahap ${current_stage}`;
  const isCancel = sub_stage === "Cancel";

  let msg;
  if (isCancel) {
    msg = `Halo ${order.customer_name},\n\nPesanan Anda dengan kode *${order.order_code || order.id}* untuk unit *${order.vehicle_name}* telah *DIBATALKAN*.\n\nMohon hubungi konsultan ${order.consultant} untuk informasi lebih lanjut.\n\n_Berburu Mobil Impian_`;
  } else if (current_stage === 7) {
    msg = `Halo ${order.customer_name},\n\nSelamat! Pesanan Anda telah *SELESAI*.\n\n📋 Kode: *${order.order_code || order.id}*\n🚗 Unit: ${order.vehicle_name}\n👤 Konsultan: ${order.consultant}\n\nTerima kasih telah mempercayakan pencarian mobil impian Anda kepada kami.\n\n_Berburu Mobil Impian_`;
  } else {
    msg = `Halo ${order.customer_name},\n\nProgres pesanan Anda telah diperbarui:\n\n📋 Kode: *${order.order_code || order.id}*\n🚗 Unit: ${order.vehicle_name}\n📍 Tahap Saat Ini: *${stageLabel}*${sub_stage ? `\n🔧 Sub-Tahap: *${sub_stage}*` : ""}\n👤 Konsultan: ${order.consultant}\n\nCek progres lengkap di portal Berburu Mobil Impian.\n\n_Berburu Mobil Impian_`;
  }

  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
}

// --- CSV helpers ---
function csvEscape(v) {
  const s = String(v ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function orderStatusLabel(o) {
  if (o.sub_stage === "Cancel") return "Cancel";
  if (o.current_stage === 7) return "Selesai";
  return `Tahap ${o.current_stage}`;
}

function downloadCSV(rows, filename) {
  const headers = ["Kode", "Tanggal Dibuat", "Status", "Nama Pemesan", "Kendaraan", "No HP", "Alamat", "Konsultan", "Catatan"];
  const lines = [headers.join(",")];
  rows.forEach(o => {
    const dt = new Date(o.created_at).toLocaleDateString("id-ID", { day: '2-digit', month: '2-digit', year: 'numeric' });
    lines.push([
      o.order_code || o.id,
      dt,
      orderStatusLabel(o),
      o.customer_name,
      o.vehicle_name,
      o.phone,
      o.address,
      o.consultant,
      o.notes || ""
    ].map(csvEscape).join(","));
  });
  const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDateTimeID(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("id-ID", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

function computeCountdown(targetDateStr) {
  if (!targetDateStr) return null;
  const target = new Date(targetDateStr + "T23:59:59");
  if (isNaN(target.getTime())) return null;
  const now = new Date();
  const diffMs = target - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  return { days, target, isOverdue: days < 0 };
}

function stageLabelById(id) {
  const s = STAGES.find(x => x.id === id);
  return s ? s.name.replace(/^\d+\.\s*/, "") : `Tahap ${id}`;
}

// --- Theme Toggle ---
function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem("bmi_theme") || "dark";
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "light") root.classList.add("theme-light");
    else root.classList.remove("theme-light");
    localStorage.setItem("bmi_theme", theme);
  }, [theme]);

  return (
    <button
      type="button"
      data-testid="theme-toggle"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      title={theme === "dark" ? "Ubah ke Mode Terang" : "Ubah ke Mode Gelap"}
      className="p-2 rounded-lg bg-[#1A1A1A] hover:bg-zinc-800 border border-[#333] transition flex items-center justify-center"
    >
      {theme === "dark"
        ? <Sun className="w-4 h-4 text-amber-400" />
        : <Moon className="w-4 h-4 text-red-500" />}
    </button>
  );
}

// --- LANDING & TRACKING PORTAL ---
function CustomerPortal() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error("Masukkan nomor handphone atau kode unik pesanan.");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/track?query=${encodeURIComponent(query)}`);
      setResults(res.data);
      if (res.data.length === 0) {
        toast.info("Tidak ada pesanan yang cocok dengan pencarian tersebut.");
      }
    } catch (err) {
      toast.error("Gagal melakukan pencarian.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col selection:bg-red-600 selection:text-white">
      {/* Header */}
      <header className="border-b border-[#222] bg-[#111]/80 backdrop-blur-xl sticky top-0 z-50 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-600 p-2.5 rounded-lg text-white shadow-lg shadow-blue-600/30">
            <Car className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-bold text-lg tracking-wider uppercase">Berburu Mobil Impian</h1>
            <p className="text-xs text-zinc-400">Real-Time Inspection & Order Tracking</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <ThemeToggle />
          <Link 
            to="/admin/login" 
            data-testid="nav-admin-login"
            className="px-4 py-2 text-sm font-medium bg-[#1A1A1A] hover:bg-zinc-800 border border-[#333] rounded-lg transition flex items-center gap-2"
          >
            <Lock className="w-4 h-4 text-red-500" />
            <span>Admin Portal</span>
          </Link>
        </div>
      </header>

      {/* Hero Search Section */}
      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-12 flex flex-col justify-center items-center text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold mb-6">
          <ShieldCheck className="w-4 h-4" /> Professional Used Car Inspection & Hunting Service
        </div>
        
        <h2 className="text-4xl sm:text-6xl font-black tracking-tight mb-4 max-w-3xl">
          Pantau Progres <span className="text-red-600">Mobil Impian</span> Anda Secara Real-Time
        </h2>
        <p className="text-zinc-400 max-w-xl mb-10 text-sm sm:text-base">
          Masukkan Nomor Handphone yang terdaftar saat registrasi atau Kode Unik Pesanan (contoh: <code className="text-red-400 bg-zinc-900 px-1.5 py-0.5 rounded">BMI-8849</code>) untuk melihat status terkini.
        </p>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="w-full max-w-2xl bg-[#141414] border border-[#2A2A2A] p-2 rounded-2xl shadow-2xl flex flex-col sm:flex-row gap-2">
          <div className="flex-1 flex items-center px-4 gap-3 bg-[#1A1A1A] rounded-xl border border-transparent focus-within:border-red-600 transition">
            <Search className="w-5 h-5 text-zinc-400 shrink-0" />
            <input 
              type="text" 
              data-testid="tracking-search-input"
              value={query} 
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari No. HP (0812...) atau Kode Pesanan (BMI-XXXX)..."
              className="w-full bg-transparent py-3 text-white placeholder-zinc-500 focus:outline-none text-sm"
            />
          </div>
          <button 
            type="submit" 
            data-testid="tracking-search-btn"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-red-600/20 shrink-0"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><span>Lacak Pesanan</span> <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        {/* Results Area */}
        {searched && (
          <div className="w-full mt-12 text-left">
            <div className="flex items-center justify-between mb-6 border-b border-[#222] pb-4">
              <h3 className="text-lg font-bold">Hasil Pencarian ({results.length})</h3>
              <span className="text-xs text-zinc-400">Pembaruan otomatis dari sistem</span>
            </div>

            {results.length === 0 ? (
              <div className="bg-[#141414] border border-[#222] rounded-2xl p-12 text-center">
                <AlertCircle className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <h4 className="font-bold text-zinc-300">Pesanan Tidak Ditemukan</h4>
                <p className="text-sm text-zinc-500 mt-1">Pastikan nomor handphone atau kode pesanan yang Anda masukkan benar.</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {results.map((order) => {
                  const countdown = computeCountdown(order.target_completion_date);
                  return (
                  <div key={order.id} className="bg-[#141414] border border-[#262626] hover:border-red-600/50 transition rounded-2xl p-6 sm:p-8 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/5 rounded-full blur-3xl pointer-events-none group-hover:bg-red-600/10 transition"></div>
                    
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                      <div>
                        <div className="flex items-center gap-3 mb-1 flex-wrap">
                          <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-3 py-1 rounded-md font-mono font-bold text-sm">
                            {order.id}
                          </span>
                          <span className="text-xs text-zinc-400">
                            Dibuat: {new Date(order.created_at).toLocaleDateString("id-ID", { day: 'numeric', month: 'long', year: 'numeric' })}
                          </span>
                        </div>
                        <h4 className="text-2xl font-black text-white">{order.vehicle_name}</h4>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-xs text-zinc-400 block">Konsultan Penanggung Jawab</span>
                          <span className="font-bold text-white flex items-center justify-end gap-1.5 mt-0.5">
                            <User className="w-4 h-4 text-red-500" /> {order.consultant}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Countdown Target */}
                    {countdown && (
                      <div 
                        data-testid={`countdown-${order.id}`}
                        className={`flex items-center gap-3 p-4 rounded-xl border mb-6 ${
                          order.sub_stage === "Cancel"
                            ? "bg-zinc-900/50 border-zinc-700 text-zinc-400"
                            : countdown.isOverdue 
                              ? "bg-red-950/30 border-red-500/40 text-red-300" 
                              : countdown.days <= 3 
                                ? "bg-amber-500/10 border-amber-500/30 text-amber-300"
                                : "bg-emerald-500/10 border-emerald-500/25 text-emerald-300"
                        }`}
                      >
                        <CalendarClock className="w-5 h-5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-[11px] uppercase tracking-wider font-bold opacity-80">Target Serah Terima</p>
                          <p className="text-sm font-bold">
                            {countdown.target.toLocaleDateString("id-ID", { day: 'numeric', month: 'long', year: 'numeric' })}
                            <span className="ml-2 font-black">
                              {order.sub_stage === "Cancel" 
                                ? "(dibatalkan)"
                                : countdown.isOverdue 
                                  ? `— lewat ${Math.abs(countdown.days)} hari` 
                                  : countdown.days === 0 
                                    ? "— hari ini!"
                                    : `— ${countdown.days} hari lagi`
                              }
                            </span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Customer & Info Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-[#1A1A1A] p-4 rounded-xl border border-[#2A2A2A] mb-8 text-sm">
                      <div>
                        <span className="text-zinc-500 text-xs block mb-0.5">Nama Pemesan</span>
                        <span className="font-semibold text-zinc-200">{order.customer_name}</span>
                      </div>
                      <div>
                        <span className="text-zinc-500 text-xs block mb-0.5">Nomor Handphone</span>
                        <span className="font-semibold text-zinc-200">{order.phone}</span>
                      </div>
                      <div className="sm:col-span-2">
                        <span className="text-zinc-500 text-xs block mb-0.5">Alamat</span>
                        <span className="font-semibold text-zinc-200">{order.address}</span>
                      </div>
                    </div>

                    {/* Timeline Progress */}
                    <div className="mb-8">
                      <h5 className="text-xs uppercase font-bold tracking-wider text-zinc-400 mb-6">Tahapan Progres Layanan</h5>
                      
                      <div className="space-y-4">
                        {STAGES.map((stg) => {
                          const isPassed = order.current_stage > stg.id;
                          const isCurrent = order.current_stage === stg.id;
                          const isCancelled = order.sub_stage === "Cancel" && stg.id === 4;
                          
                          return (
                            <div key={stg.id} className={`flex items-start gap-4 p-4 rounded-xl border transition ${
                              isCurrent 
                                ? (isCancelled ? "bg-red-950/20 border-red-600/50" : "bg-red-500/10 border-red-500/40 shadow-lg shadow-red-600/10") 
                                : isPassed 
                                  ? "bg-[#181818] border-[#2E2E2E] opacity-90" 
                                  : "bg-[#121212] border-[#202020] opacity-40"
                            }`}>
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                                isCurrent 
                                  ? (isCancelled ? "bg-red-900 text-white" : "bg-red-600 text-white shadow-md shadow-red-600/30") 
                                  : isPassed 
                                    ? "bg-zinc-800 text-emerald-400" 
                                    : "bg-zinc-900 text-zinc-600"
                              }`}>
                                {isPassed ? <Check className="w-5 h-5" /> : <stg.icon className="w-5 h-5" />}
                              </div>
                              
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <h6 className={`font-bold ${isCurrent ? "text-white text-base" : "text-zinc-300 text-sm"}`}>
                                    {stg.name}
                                  </h6>
                                  {isCurrent && (
                                    <span className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                                      isCancelled ? "bg-red-600 text-white" : "bg-red-600 text-white animate-pulse"
                                    }`}>
                                      {isCancelled ? "DIBATALKAN" : "PROSES SAAT INI"}
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-zinc-400 mt-1">{stg.desc}</p>

                                {/* If Stage 4, show sub-stages detail */}
                                {stg.id === 4 && (order.current_stage >= 4 || order.sub_stage) && (
                                  <div className="mt-4 pt-3 border-t border-[#2A2A2A]/60">
                                    <span className="text-[11px] font-bold text-zinc-400 uppercase tracking-wider block mb-2">
                                      Sub-Tahap Dalam Proses:
                                    </span>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                      {IN_PROCESS_SUBSTAGES.map((sub) => {
                                        const isSubActive = order.sub_stage === sub;
                                        const isSubPassed = IN_PROCESS_SUBSTAGES.indexOf(order.sub_stage) > IN_PROCESS_SUBSTAGES.indexOf(sub) && order.sub_stage !== "Cancel";
                                        
                                        return (
                                          <div key={sub} className={`p-2.5 rounded-lg border text-xs text-center transition ${
                                            isSubActive 
                                              ? (sub === "Cancel" ? "bg-red-600 text-white font-bold border-red-500 shadow-lg" : "bg-red-600 text-white font-bold border-red-500 shadow-lg") 
                                              : isSubPassed 
                                                ? "bg-zinc-800 text-zinc-300 border-zinc-700" 
                                                : "bg-zinc-900/60 text-zinc-500 border-zinc-800"
                                          }`}>
                                            {sub}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* History Timeline */}
                    {Array.isArray(order.history) && order.history.length > 0 && (
                      <div className="mb-8" data-testid={`history-${order.id}`}>
                        <h5 className="text-xs uppercase font-bold tracking-wider text-zinc-400 mb-4 flex items-center gap-2">
                          <History className="w-3.5 h-3.5" /> Riwayat Perubahan Progres
                        </h5>
                        <div className="relative pl-6 border-l-2 border-[#2A2A2A] space-y-4">
                          {[...order.history].reverse().map((h, idx) => (
                            <div key={`${h.timestamp}-${h.stage}-${h.sub_stage}-${idx}`} className="relative">
                              <div className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 ${
                                idx === 0 
                                  ? (h.sub_stage === "Cancel" ? "bg-red-600 border-red-400" : "bg-red-600 border-red-400 shadow-lg shadow-red-600/40")
                                  : "bg-zinc-700 border-zinc-600"
                              }`}></div>
                              <div className="bg-[#181818] border border-[#262626] rounded-xl p-3">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${
                                    h.sub_stage === "Cancel" 
                                      ? "bg-red-600 text-white" 
                                      : "bg-red-500/10 text-red-400 border border-red-500/20"
                                  }`}>
                                    Tahap {h.stage}: {stageLabelById(h.stage)}
                                    {h.sub_stage && ` • ${h.sub_stage}`}
                                  </span>
                                  <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {formatDateTimeID(h.timestamp)}
                                  </span>
                                  {h.updated_by && (
                                    <span className="text-[11px] text-zinc-400">
                                      oleh <span className="font-bold text-zinc-200">{h.updated_by}</span>
                                    </span>
                                  )}
                                </div>
                                {h.notes && <p className="text-xs text-zinc-400 mt-1">{h.notes}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Notes Box */}
                    {order.notes && (
                      <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-4 rounded-xl">
                        <span className="text-xs font-bold text-zinc-400 uppercase tracking-wider block mb-1">Catatan Tim Konsultan</span>
                        <p className="text-sm text-zinc-300">{order.notes}</p>
                      </div>
                    )}
                  </div>
                );})}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-[#222] py-8 text-center text-xs text-zinc-500 bg-[#111]">
        <p>&copy; {new Date().getFullYear()} Berburu Mobil Impian. All rights reserved. Professional Automotive Inspection & Hunting.</p>
      </footer>
    </div>
  );
}

// --- ADMIN LOGIN ---
function AdminLogin() {
  const [username, setUsername] = useState("");
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { username, pin });
      if (res.data.success) {
        localStorage.setItem("bmi_admin_logged", "true");
        localStorage.setItem("bmi_admin_username", username);
        toast.success("Login admin berhasil!");
        navigate("/admin/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Username atau PIN salah.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col justify-center items-center p-6 selection:bg-red-600 selection:text-white relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md bg-[#141414] border border-[#262626] p-8 rounded-2xl shadow-2xl relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 rounded-full blur-3xl pointer-events-none"></div>
        
        <div className="text-center mb-8">
          <div className="bg-red-600 w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-red-600/30">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-black tracking-tight">Admin Portal</h2>
          <p className="text-xs text-zinc-400 mt-1">Berburu Mobil Impian - Dashboard Kontrol</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">Username Admin</label>
            <input 
              type="text" 
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Masukkan username"
              required
              className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-3 text-white text-sm focus:outline-none transition"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">PIN Admin</label>
            <input 
              type="password" 
              data-testid="admin-pin-input"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Masukkan PIN 4-digit"
              required
              className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-3 text-white text-sm focus:outline-none transition"
            />
          </div>

          <button 
            type="submit" 
            data-testid="admin-login-submit"
            className="w-full bg-red-600 hover:bg-red-700 text-white py-3.5 rounded-xl font-bold text-sm transition shadow-lg shadow-red-600/20 flex items-center justify-center gap-2 mt-4"
          >
            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <span>Masuk Dashboard</span>}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="text-xs text-zinc-400 hover:text-white transition flex items-center justify-center gap-1.5">
            <ChevronLeft className="w-4 h-4" /> Kembali ke Portal Kustomer
          </Link>
        </div>
      </div>
    </div>
  );
}

// --- ADMIN DASHBOARD ---
function AdminDashboard() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [consultantFilter, setConsultantFilter] = useState("all");
  const [search, setSearch] = useState("");
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showEditStageModal, setShowEditStageModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState({
    customer_name: "",
    vehicle_name: "",
    address: "",
    phone: "",
    consultant: "Agus",
    target_completion_date: "",
    notes: ""
  });

  // Export options
  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
  const [exportMonth, setExportMonth] = useState(currentMonth);
  const [exportStatus, setExportStatus] = useState("both"); // selesai | cancel | both

  // New order form
  const [newOrder, setNewOrder] = useState({
    customer_name: "",
    vehicle_name: "",
    address: "",
    phone: "",
    consultant: "Agus",
    notes: "",
    target_completion_date: ""
  });

  // Edit stage form
  const [editStageData, setEditStageData] = useState({
    current_stage: 1,
    sub_stage: "",
    notes: "",
    target_completion_date: ""
  });

  // Admin credentials state
  const [adminCreds, setAdminCreds] = useState({ username: "admin" });
  const [newCreds, setNewCreds] = useState({ username: "admin", pin: "" });

  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders`);
      setOrders(res.data);
      const credRes = await axios.get(`${API}/admin/credentials`);
      setAdminCreds(credRes.data);
      setNewCreds({ username: credRes.data.username || "admin", pin: "" });
    } catch (err) {
      toast.error("Gagal mengambil data pesanan.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("bmi_admin_logged") !== "true") {
      navigate("/admin/login");
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => {
    localStorage.removeItem("bmi_admin_logged");
    toast.success("Berhasil keluar dari admin.");
    navigate("/admin/login");
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    try {
      const adminUser = localStorage.getItem("bmi_admin_username") || "admin";
      await axios.post(`${API}/orders`, { ...newOrder, created_by: adminUser });
      toast.success("Pesanan baru berhasil dibuat!");
      setShowCreateModal(false);
      setNewOrder({ customer_name: "", vehicle_name: "", address: "", phone: "", consultant: "Agus", notes: "", target_completion_date: "" });
      fetchData();
    } catch (err) {
      toast.error("Gagal membuat pesanan.");
    }
  };

  const handleUpdateStageSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    try {
      const adminUser = localStorage.getItem("bmi_admin_username") || "admin";
      const payload = { ...editStageData, updated_by: adminUser };
      const res = await axios.put(`${API}/orders/${selectedOrder.id}/stage`, payload);
      const updated = res.data.order || { ...selectedOrder, ...editStageData };
      toast.success(`Progres pesanan ${selectedOrder.id} berhasil diperbarui!`);

      // Auto-open WhatsApp deep-link with pre-filled update message
      const waUrl = buildWhatsAppUrl(updated, editStageData.current_stage, editStageData.current_stage === 4 ? editStageData.sub_stage : "");
      window.open(waUrl, "_blank", "noopener,noreferrer");

      setShowEditStageModal(false);
      setSelectedOrder(null);
      fetchData();
    } catch (err) {
      toast.error("Gagal memperbarui progres.");
    }
  };

  const handleSendWhatsApp = (order) => {
    const waUrl = buildWhatsAppUrl(order, order.current_stage, order.sub_stage);
    window.open(waUrl, "_blank", "noopener,noreferrer");
  };

  const getExportRows = () => {
    return orders.filter(o => {
      if (!o.created_at) return false;
      const ym = new Date(o.created_at).toISOString().slice(0, 7);
      if (ym !== exportMonth) return false;
      const isCancel = o.sub_stage === "Cancel";
      const isSelesai = o.current_stage === 7 && !isCancel;
      if (exportStatus === "cancel") return isCancel;
      if (exportStatus === "selesai") return isSelesai;
      return isCancel || isSelesai;
    });
  };

  const handleDownloadCSV = () => {
    const rows = getExportRows();
    if (rows.length === 0) {
      toast.info("Tidak ada pesanan yang cocok dengan filter ini.");
      return;
    }
    const fname = `BMI_${exportMonth}_${exportStatus}.csv`;
    downloadCSV(rows, fname);
    toast.success(`${rows.length} pesanan diekspor ke ${fname}`);
    setShowExportModal(false);
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm(`Hapus pesanan ${id}?`)) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success("Pesanan berhasil dihapus.");
      fetchData();
    } catch (err) {
      toast.error("Gagal menghapus pesanan.");
    }
  };

  const handleUpdateDetailSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrder) return;
    try {
      await axios.put(`${API}/orders/${selectedOrder.id}`, detailData);
      toast.success(`Detail pesanan ${selectedOrder.id} berhasil disimpan!`);
      setShowDetailModal(false);
      setSelectedOrder(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan detail pesanan.");
    }
  };

  const handleUpdateCredentials = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/credentials`, newCreds);
      toast.success("Username & PIN admin berhasil diperbarui!");
      setAdminCreds({ username: newCreds.username });
      setNewCreds({ username: newCreds.username, pin: "" });
      setShowSettingsModal(false);
    } catch (err) {
      toast.error("Gagal memperbarui kredensial admin.");
    }
  };

  const filteredOrders = orders.filter(o => {
    const matchesSearch = o.customer_name.toLowerCase().includes(search.toLowerCase()) ||
                          o.vehicle_name.toLowerCase().includes(search.toLowerCase()) ||
                          o.id.toLowerCase().includes(search.toLowerCase()) ||
                          o.phone.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (consultantFilter !== "all" && o.consultant !== consultantFilter) return false;
    if (filter === "all") return true;
    if (filter === "cancel") return o.sub_stage === "Cancel";
    return o.current_stage.toString() === filter;
  });

  // Consultant stats: aktif = tahap 1-6 non-cancel; tuntas = tahap 7; cancel = sub Cancel
  const consultantStats = ["Agus", "Brenkhi"].map(name => {
    const owned = orders.filter(o => o.consultant === name);
    return {
      name,
      aktif: owned.filter(o => o.sub_stage !== "Cancel" && o.current_stage >= 1 && o.current_stage <= 6).length,
      tuntas: owned.filter(o => o.sub_stage !== "Cancel" && o.current_stage === 7).length,
      cancel: owned.filter(o => o.sub_stage === "Cancel").length,
      total: owned.length,
    };
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col selection:bg-red-600 selection:text-white">
      {/* Top Bar */}
      <header className="border-b border-[#222] bg-[#111]/90 backdrop-blur-xl sticky top-0 z-50 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-red-600 p-2.5 rounded-lg text-white shadow-lg shadow-red-600/30">
            <Car className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-bold text-base tracking-wider uppercase">Dashboard Admin</h1>
            <p className="text-xs text-zinc-400">Berburu Mobil Impian Control Center</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <button 
            data-testid="admin-settings-btn"
            onClick={() => setShowSettingsModal(true)}
            className="px-3.5 py-2 text-xs font-semibold bg-[#1A1A1A] hover:bg-zinc-800 border border-[#333] rounded-lg transition flex items-center gap-2"
          >
            <Settings className="w-4 h-4 text-zinc-400" />
            <span>Ganti Username & PIN</span>
          </button>
          
          <button 
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="px-3.5 py-2 text-xs font-semibold bg-red-600/10 hover:bg-red-600/20 text-red-400 border border-red-500/20 rounded-lg transition flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />
            <span>Keluar</span>
          </button>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-8">
        {/* Consultant Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-testid="consultant-stats">
          {consultantStats.map(s => {
            const isActive = consultantFilter === s.name;
            return (
              <button 
                key={s.name} 
                type="button"
                data-testid={`stats-${s.name.toLowerCase()}`}
                onClick={() => setConsultantFilter(isActive ? "all" : s.name)}
                title={isActive ? "Klik untuk hapus filter" : `Filter pesanan konsultan ${s.name}`}
                className={`bg-[#141414] border rounded-2xl p-5 flex items-center justify-between gap-4 text-left transition ${
                  isActive
                    ? "border-red-500/60 shadow-lg shadow-red-600/10 ring-2 ring-red-500/30"
                    : "border-[#262626] hover:border-[#3A3A3A]"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`border w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                    isActive ? "bg-red-600 border-red-500 text-white" : "bg-red-600/15 border-red-500/30 text-red-500"
                  }`}>
                    <User className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">
                      {isActive ? "Filter Aktif" : "Konsultan"}
                    </p>
                    <h4 className="text-xl font-black text-white truncate">{s.name}</h4>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-center">
                  <div>
                    <p className="text-2xl font-black text-white leading-none" data-testid={`stats-${s.name.toLowerCase()}-aktif`}>{s.aktif}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Aktif</p>
                  </div>
                  <div className="w-px h-8 bg-[#2A2A2A]"></div>
                  <div>
                    <p className="text-2xl font-black text-emerald-400 leading-none" data-testid={`stats-${s.name.toLowerCase()}-tuntas`}>{s.tuntas}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Tuntas</p>
                  </div>
                  <div className="w-px h-8 bg-[#2A2A2A]"></div>
                  <div>
                    <p className="text-2xl font-black text-red-500 leading-none" data-testid={`stats-${s.name.toLowerCase()}-cancel`}>{s.cancel}</p>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">Cancel</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Action Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl font-black">Manajemen Pesanan Kustomer</h2>
            <p className="text-xs text-zinc-400 mt-1">Majukan atau mundurkan progres pesanan secara real-time.</p>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            <button 
              data-testid="admin-export-csv-btn"
              onClick={() => setShowExportModal(true)}
              className="bg-[#1A1A1A] hover:bg-zinc-800 border border-[#333] text-white px-4 py-2.5 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4 text-red-500" />
              <span>Ekspor CSV</span>
            </button>

            <button 
              data-testid="admin-create-order-btn"
              onClick={() => setShowCreateModal(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition flex items-center justify-center gap-2 shadow-lg shadow-red-600/20"
            >
              <PlusCircle className="w-4 h-4" />
              <span>Input Pesanan Baru</span>
            </button>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-[#141414] border border-[#222] p-4 rounded-2xl mb-6 space-y-3">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="w-full md:w-80 relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3.5 top-3" />
              <input 
                type="text" 
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cari nama, mobil, kode, no hp..."
                className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
              <span className="text-xs text-zinc-500 font-semibold shrink-0">Konsultan:</span>
              <button 
                onClick={() => setConsultantFilter("all")} 
                data-testid="admin-filter-consultant-all"
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${consultantFilter === "all" ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white border border-[#2A2A2A]"}`}
              >
                Semua
              </button>
              <button 
                onClick={() => setConsultantFilter("Agus")} 
                data-testid="admin-filter-consultant-agus"
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${consultantFilter === "Agus" ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white border border-[#2A2A2A]"}`}
              >
                Agus
              </button>
              <button 
                onClick={() => setConsultantFilter("Brenkhi")} 
                data-testid="admin-filter-consultant-brenkhi"
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${consultantFilter === "Brenkhi" ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white border border-[#2A2A2A]"}`}
              >
                Brenkhi
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0 border-t border-[#222] pt-3">
            <span className="text-xs text-zinc-500 font-semibold shrink-0">Filter Tahap:</span>
            <button 
              onClick={() => setFilter("all")} 
              data-testid="admin-filter-all"
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${filter === "all" ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white"}`}
            >
              Semua ({orders.length})
            </button>
            {STAGES.map((stg) => (
              <button 
                key={stg.id} 
                onClick={() => setFilter(stg.id.toString())} 
                data-testid={`admin-filter-stage-${stg.id}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${filter === stg.id.toString() ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white"}`}
              >
                Tahap {stg.id}
              </button>
            ))}
            <button 
              onClick={() => setFilter("cancel")} 
              data-testid="admin-filter-cancel"
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition whitespace-nowrap ${filter === "cancel" ? "bg-red-600 text-white" : "bg-[#1A1A1A] text-zinc-400 hover:text-white"}`}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Orders Table / Cards */}
        {loading ? (
          <div className="text-center py-20 text-zinc-500">Memuat data pesanan...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="bg-[#141414] border border-[#222] rounded-2xl p-16 text-center">
            <Car className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
            <h4 className="font-bold text-zinc-300">Tidak ada pesanan ditemukan</h4>
            <p className="text-sm text-zinc-500 mt-1">Coba ubah filter atau input pesanan baru.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredOrders.map((order) => {
              const currentStageObj = STAGES.find(s => s.id === order.current_stage);
              const isCancelled = order.sub_stage === "Cancel";

              return (
                <div key={order.id} className="bg-[#141414] border border-[#262626] hover:border-[#333] transition rounded-2xl p-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-0.5 rounded font-mono font-bold text-xs">
                        {order.id}
                      </span>
                      <span className={`text-xs px-2.5 py-0.5 rounded font-bold ${
                        isCancelled ? "bg-red-900 text-white" : "bg-zinc-800 text-zinc-300"
                      }`}>
                        {isCancelled ? "DIBATALKAN" : `Tahap ${order.current_stage}: ${currentStageObj?.name.replace(/^\d+\.\s*/, '')}`}
                      </span>
                      {order.sub_stage && order.sub_stage !== "Cancel" && (
                        <span className="text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2.5 py-0.5 rounded font-semibold">
                          Sub: {order.sub_stage}
                        </span>
                      )}
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-black text-white">{order.vehicle_name}</h3>
                      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-zinc-400 mt-1">
                        <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-red-500" /> {order.customer_name}</span>
                        <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-red-500" /> {order.phone}</span>
                        <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-red-500" /> {order.address}</span>
                        <span className="font-semibold text-zinc-300">Konsultan: {order.consultant}</span>
                      </div>
                    </div>

                    {order.notes && (
                      <p className="text-xs text-zinc-400 bg-[#1A1A1A] p-2.5 rounded-lg border border-[#2A2A2A] mt-2">
                        <span className="font-bold text-zinc-300">Catatan:</span> {order.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 self-end lg:self-center shrink-0">
                    <button 
                      data-testid={`admin-wa-${order.id}`}
                      onClick={() => handleSendWhatsApp(order)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition shadow-md shadow-emerald-600/20 flex items-center gap-1.5"
                      title="Kirim update via WhatsApp"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> Kirim WA
                    </button>

                    <button 
                      data-testid={`admin-detail-${order.id}`}
                      onClick={() => {
                        setSelectedOrder(order);
                        setDetailData({
                          customer_name: order.customer_name || "",
                          vehicle_name: order.vehicle_name || "",
                          address: order.address || "",
                          phone: order.phone || "",
                          consultant: order.consultant || "Agus",
                          target_completion_date: order.target_completion_date || "",
                          notes: order.notes || ""
                        });
                        setShowDetailModal(true);
                      }}
                      className="bg-[#1A1A1A] hover:bg-zinc-800 border border-[#333] text-white px-3.5 py-2 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
                      title="Lihat & Edit Detail"
                    >
                      <Pencil className="w-3.5 h-3.5 text-red-500" /> Detail
                    </button>

                    <button 
                      data-testid={`admin-edit-stage-${order.id}`}
                      onClick={() => {
                        setSelectedOrder(order);
                        setEditStageData({
                          current_stage: order.current_stage,
                          sub_stage: order.sub_stage || "Pencarian Iklan",
                          notes: order.notes || "",
                          target_completion_date: order.target_completion_date || ""
                        });
                        setShowEditStageModal(true);
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl text-xs font-bold transition shadow-md shadow-red-600/20 flex items-center gap-1.5"
                    >
                      <RefreshCw className="w-3.5 h-3.5" /> Atur Progres
                    </button>

                    <button 
                      data-testid={`admin-delete-${order.id}`}
                      onClick={() => handleDeleteOrder(order.id)}
                      className="bg-zinc-800 hover:bg-red-950 text-zinc-400 hover:text-red-400 p-2 rounded-xl transition border border-[#333]"
                      title="Hapus Pesanan"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* CREATE ORDER MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-1">Input Pesanan Baru</h3>
            <p className="text-xs text-zinc-400 mb-6">Masukkan data pesanan kustomer secara manual.</p>

            <form onSubmit={handleCreateOrder} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Nama Pemesan</label>
                <input 
                  type="text" 
                  data-testid="create-customer-name"
                  value={newOrder.customer_name} 
                  onChange={(e) => setNewOrder({...newOrder, customer_name: e.target.value})}
                  placeholder="Contoh: Budi Santoso"
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Kendaraan yang Dipesan</label>
                <input 
                  type="text" 
                  data-testid="create-vehicle-name"
                  value={newOrder.vehicle_name} 
                  onChange={(e) => setNewOrder({...newOrder, vehicle_name: e.target.value})}
                  placeholder="Contoh: Toyota Alphard 2.5 G 2021"
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Alamat</label>
                <textarea 
                  data-testid="create-address"
                  value={newOrder.address} 
                  onChange={(e) => setNewOrder({...newOrder, address: e.target.value})}
                  placeholder="Contoh: Jl. Sudirman No. 45, Jakarta Selatan"
                  required
                  rows={2}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none resize-none"
                ></textarea>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Nomor Handphone</label>
                <input 
                  type="text" 
                  data-testid="create-phone"
                  value={newOrder.phone} 
                  onChange={(e) => setNewOrder({...newOrder, phone: e.target.value})}
                  placeholder="Contoh: 081234567890"
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Konsultan yang Menangani</label>
                <select 
                  data-testid="create-consultant"
                  value={newOrder.consultant} 
                  onChange={(e) => setNewOrder({...newOrder, consultant: e.target.value})}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                >
                  <option value="Agus">Agus</option>
                  <option value="Brenkhi">Brenkhi</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Target Tanggal Serah Terima (Opsional)</label>
                <input 
                  type="date" 
                  data-testid="create-target-date"
                  value={newOrder.target_completion_date} 
                  onChange={(e) => setNewOrder({...newOrder, target_completion_date: e.target.value})}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Catatan Awal (Opsional)</label>
                <input 
                  type="text" 
                  data-testid="create-notes"
                  value={newOrder.notes} 
                  onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
                  placeholder="Contoh: Konsultasi awal via WhatsApp."
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs font-bold transition"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  data-testid="create-order-submit"
                  className="px-6 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-xs font-bold transition shadow-lg shadow-red-600/20"
                >
                  Simpan Pesanan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT STAGE MODAL */}
      {showEditStageModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-1">Atur Progres Pesanan</h3>
            <p className="text-xs text-zinc-400 mb-4">Pesanan: <span className="text-red-400 font-mono font-bold">{selectedOrder.id}</span> - {selectedOrder.vehicle_name}</p>

            <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-start gap-2.5 text-emerald-300">
              <MessageCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <p className="text-xs leading-relaxed">Setelah disimpan, tab WhatsApp akan otomatis terbuka dengan pesan update siap kirim ke <span className="font-bold">{selectedOrder.phone}</span>.</p>
            </div>

            <form onSubmit={handleUpdateStageSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">Pilih Tahap Utama (1 - 7)</label>
                <select 
                  data-testid="edit-current-stage"
                  value={editStageData.current_stage} 
                  onChange={(e) => setEditStageData({...editStageData, current_stage: parseInt(e.target.value)})}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                >
                  {STAGES.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* If stage 4 is selected, show sub-stages including Cancel */}
              {editStageData.current_stage === 4 && (
                <div className="bg-[#1A1A1A] p-4 rounded-xl border border-[#2A2A2A] space-y-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider text-amber-400 mb-1">Sub-Tahap Dalam Proses & Cancel</label>
                  <select 
                    data-testid="edit-sub-stage"
                    value={editStageData.sub_stage} 
                    onChange={(e) => setEditStageData({...editStageData, sub_stage: e.target.value})}
                    className="w-full bg-[#121212] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                  >
                    <option value="Pencarian Iklan">Pencarian Iklan</option>
                    <option value="Inspeksi Unit">Inspeksi Unit</option>
                    <option value="Eksekusi Unit & Pelunasan">Eksekusi Unit & Pelunasan</option>
                    <option value="Cancel">Cancel (Dibatalkan Kustomer/Tim)</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Target Tanggal Serah Terima</label>
                <input 
                  type="date" 
                  data-testid="edit-target-date"
                  value={editStageData.target_completion_date} 
                  onChange={(e) => setEditStageData({...editStageData, target_completion_date: e.target.value})}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Catatan Lapangan / Update</label>
                <textarea 
                  data-testid="edit-notes"
                  value={editStageData.notes} 
                  onChange={(e) => setEditStageData({...editStageData, notes: e.target.value})}
                  rows={3}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none resize-none"
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowEditStageModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs font-bold transition"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  data-testid="edit-stage-submit"
                  className="px-6 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-xs font-bold transition shadow-lg shadow-red-600/20"
                >
                  Perbarui Progres
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SETTINGS MODAL (CHANGE ADMIN USERNAME & PIN) */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-md w-full p-6 sm:p-8 relative shadow-2xl">
            <h3 className="text-xl font-bold mb-1">Pengaturan Akun Admin</h3>
            <p className="text-xs text-zinc-400 mb-6">Ubah username dan PIN untuk login dashboard.</p>

            <form onSubmit={handleUpdateCredentials} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Username Baru</label>
                <input 
                  type="text" 
                  data-testid="settings-username"
                  value={newCreds.username} 
                  onChange={(e) => setNewCreds({...newCreds, username: e.target.value})}
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">PIN Baru (4-digit atau teks)</label>
                <input 
                  type="text" 
                  data-testid="settings-pin"
                  value={newCreds.pin} 
                  onChange={(e) => setNewCreds({...newCreds, pin: e.target.value})}
                  placeholder="Masukkan PIN baru"
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs font-bold transition"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  data-testid="settings-submit"
                  className="px-6 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-xs font-bold transition shadow-lg shadow-red-600/20"
                >
                  Simpan Perubahan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EXPORT CSV MODAL */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-md w-full p-6 sm:p-8 relative shadow-2xl">
            <h3 className="text-xl font-bold mb-1">Ekspor Laporan Pesanan</h3>
            <p className="text-xs text-zinc-400 mb-6">Unduh arsip pesanan Selesai atau Cancel dalam format CSV per bulan.</p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">Bulan Pesanan</label>
                <input 
                  type="month" 
                  data-testid="export-month"
                  value={exportMonth} 
                  onChange={(e) => setExportMonth(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1.5">Status Pesanan</label>
                <select 
                  data-testid="export-status"
                  value={exportStatus} 
                  onChange={(e) => setExportStatus(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                >
                  <option value="both">Selesai + Cancel</option>
                  <option value="selesai">Selesai saja</option>
                  <option value="cancel">Cancel saja</option>
                </select>
              </div>

              <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-3 text-xs text-zinc-400">
                <span className="font-bold text-white">{getExportRows().length}</span> pesanan cocok dengan filter ini.
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs font-bold transition"
                >
                  Batal
                </button>
                <button 
                  type="button"
                  onClick={handleDownloadCSV}
                  data-testid="export-csv-submit"
                  className="px-6 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-xs font-bold transition shadow-lg shadow-red-600/20 flex items-center gap-2"
                >
                  <Download className="w-4 h-4" /> Download CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DETAIL / EDIT ORDER MODAL */}
      {showDetailModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#141414] border border-[#262626] rounded-2xl max-w-3xl w-full p-6 sm:p-8 relative shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-xl font-bold mb-1">Detail Pesanan</h3>
                <p className="text-xs text-zinc-400">
                  <span className="text-red-400 font-mono font-bold">{selectedOrder.id}</span>
                  {" • "}
                  <span className="text-zinc-300">Tahap {selectedOrder.current_stage}: {stageLabelById(selectedOrder.current_stage)}</span>
                  {selectedOrder.sub_stage && <span className="text-amber-400"> • {selectedOrder.sub_stage}</span>}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowDetailModal(false)}
                className="text-zinc-500 hover:text-white transition"
                title="Tutup"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleUpdateDetailSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Nama Pemesan</label>
                  <input 
                    type="text" 
                    data-testid="detail-customer-name"
                    value={detailData.customer_name} 
                    onChange={(e) => setDetailData({...detailData, customer_name: e.target.value})}
                    required
                    className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Nomor Handphone</label>
                  <input 
                    type="text" 
                    data-testid="detail-phone"
                    value={detailData.phone} 
                    onChange={(e) => setDetailData({...detailData, phone: e.target.value})}
                    required
                    className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Kendaraan yang Dipesan</label>
                <input 
                  type="text" 
                  data-testid="detail-vehicle-name"
                  value={detailData.vehicle_name} 
                  onChange={(e) => setDetailData({...detailData, vehicle_name: e.target.value})}
                  required
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Alamat</label>
                <textarea 
                  data-testid="detail-address"
                  value={detailData.address} 
                  onChange={(e) => setDetailData({...detailData, address: e.target.value})}
                  required
                  rows={2}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none resize-none"
                ></textarea>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Konsultan</label>
                  <select 
                    data-testid="detail-consultant"
                    value={detailData.consultant} 
                    onChange={(e) => setDetailData({...detailData, consultant: e.target.value})}
                    className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                  >
                    <option value="Agus">Agus</option>
                    <option value="Brenkhi">Brenkhi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Target Tanggal Serah Terima</label>
                  <input 
                    type="date" 
                    data-testid="detail-target-date"
                    value={detailData.target_completion_date} 
                    onChange={(e) => setDetailData({...detailData, target_completion_date: e.target.value})}
                    className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-1">Catatan Terkini</label>
                <textarea 
                  data-testid="detail-notes"
                  value={detailData.notes} 
                  onChange={(e) => setDetailData({...detailData, notes: e.target.value})}
                  rows={2}
                  className="w-full bg-[#1A1A1A] border border-[#333] focus:border-red-600 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none resize-none"
                ></textarea>
              </div>

              {/* Read-only info */}
              <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-3 text-xs text-zinc-400 flex flex-wrap gap-x-6 gap-y-1">
                <span>Dibuat: <span className="text-zinc-200 font-semibold">{formatDateTimeID(selectedOrder.created_at)}</span></span>
                <span>Total Perubahan: <span className="text-zinc-200 font-semibold">{Array.isArray(selectedOrder.history) ? selectedOrder.history.length : 0}</span></span>
              </div>

              {/* Compact history */}
              {Array.isArray(selectedOrder.history) && selectedOrder.history.length > 0 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-400 mb-2 flex items-center gap-2">
                    <History className="w-3.5 h-3.5" /> Riwayat Perubahan
                  </label>
                  <div className="max-h-48 overflow-y-auto bg-[#0F0F0F] border border-[#2A2A2A] rounded-xl p-2 space-y-1.5" data-testid="detail-history">
                    {[...selectedOrder.history].reverse().map((h, idx) => (
                      <div key={`${h.timestamp}-${h.stage}-${h.sub_stage}-${idx}`} className="text-xs text-zinc-300 py-1.5 px-2 rounded-lg bg-[#181818] flex flex-wrap items-center gap-x-3 gap-y-1">
                        <span className="bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap">
                          T{h.stage}{h.sub_stage ? ` • ${h.sub_stage}` : ""}
                        </span>
                        <span className="text-zinc-500 text-[11px] flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatDateTimeID(h.timestamp)}
                        </span>
                        <span className="text-zinc-400 text-[11px]">oleh <span className="text-zinc-200 font-bold">{h.updated_by}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 rounded-xl bg-zinc-800 text-zinc-300 hover:bg-zinc-700 text-xs font-bold transition"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  data-testid="detail-submit"
                  className="px-6 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 text-xs font-bold transition shadow-lg shadow-red-600/20"
                >
                  Simpan Detail
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="border-t border-[#222] py-6 text-center text-xs text-zinc-500 bg-[#111]">
        <p>&copy; {new Date().getFullYear()} Berburu Mobil Impian - Admin Control Panel</p>
      </footer>
    </div>
  );
}

// --- MAIN APP ROUTER ---
function App() {
  // Apply persisted theme on mount before first paint
  useEffect(() => {
    const saved = localStorage.getItem("bmi_theme");
    if (saved === "light") document.documentElement.classList.add("theme-light");
  }, []);

  return (
    <BrowserRouter>
      <Toaster position="bottom-right" richColors />
      <Routes>
        <Route path="/" element={<CustomerPortal />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
